import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import {
  TrendingUp, TrendingDown, Euro, ShoppingBag, Receipt, Percent, XCircle, Users,
  Clock, Star, Download, Flame, Rocket, Package,
} from "lucide-react";

const PERIODS = [["7d", "7 giorni"], ["30d", "30 giorni"], ["90d", "90 giorni"]];
const HOURS = Array.from({ length: 16 }, (_, i) => i + 8); // 08:00 → 23:00
const WD = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function Delta({ v }) {
  if (v === null || v === undefined) return null;
  const up = v >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${up ? "text-[#00E676]" : "text-red-400"}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{up ? "+" : ""}{v}%
    </span>
  );
}

function Kpi({ label, value, icon: Icon, delta, accent, testid }) {
  return (
    <div data-testid={testid} className={`rounded-2xl p-5 border ${accent ? "border-[#00E676]/30 bg-gradient-to-br from-[#00E676]/10 to-[#131316]" : "border-white/10 bg-[#1C1C1F]"}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400"><Icon className={`h-4 w-4 ${accent ? "text-[#00E676]" : "text-zinc-400"}`} /> {label}</div>
      <div className="flex items-end justify-between mt-2">
        <div className={`text-2xl font-display font-bold ${accent ? "text-[#00E676]" : "text-white"}`}>{value}</div>
        <Delta v={delta} />
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children, right }) {
  return (
    <div className="rounded-2xl p-5 border border-white/10 bg-[#1C1C1F]">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs uppercase tracking-widest text-zinc-400 flex items-center gap-2">{Icon && <Icon className="h-4 w-4 text-[#00E676]" />} {title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function BusinessAnalytics() {
  const [period, setPeriod] = useState("30d");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/business/analytics?period=${period}`).then(({ data }) => setD(data)).catch(() => setD(null)).finally(() => setLoading(false));
  }, [period]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/business/analytics/export.csv?period=${period}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `report_attivita_${period}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { /* ignore */ }
  };

  if (loading || !d) return <div className="text-zinc-500 py-16 text-center" data-testid="analytics-loading">Caricamento Business Intelligence…</div>;

  const c = d.current, dl = d.deltas || {};
  const eur = (n) => `€${Number(n || 0).toFixed(2)}`;
  const heatMax = Math.max(1, ...(d.heatmap || []).map((h) => h.count));
  const heatAt = (w, h) => (d.heatmap || []).find((x) => x.weekday === w && x.hour === h)?.count || 0;

  return (
    <div className="space-y-6" data-testid="business-analytics">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-display font-bold">Business Intelligence</h2>
          <p className="text-sm text-zinc-500">Dati reali del tuo negozio · confronto con il periodo precedente</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-white/10 overflow-hidden">
            {PERIODS.map(([k, label]) => (
              <button key={k} data-testid={`period-${k}`} onClick={() => setPeriod(k)} className={`text-xs px-3 py-2 ${period === k ? "bg-[#00E676] text-black font-semibold" : "text-zinc-400 hover:text-white"}`}>{label}</button>
            ))}
          </div>
          <Button size="sm" variant="secondary" data-testid="analytics-export" onClick={exportCsv}><Download className="h-4 w-4 mr-1" /> CSV</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-revenue" label="Fatturato" value={eur(c.revenue)} icon={Euro} delta={dl.revenue} accent />
        <Kpi testid="kpi-net" label="Utile netto" value={eur(c.net_profit)} icon={TrendingUp} delta={dl.net_profit} />
        <Kpi testid="kpi-orders" label="Ordini" value={c.orders} icon={ShoppingBag} delta={dl.orders} />
        <Kpi testid="kpi-ticket" label="Ticket medio" value={eur(c.avg_ticket)} icon={Receipt} delta={dl.avg_ticket} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-conversion" label="Conversione" value={`${c.conversion_rate}%`} icon={Percent} delta={dl.conversion_rate} />
        <Kpi testid="kpi-cancel" label="Annullamenti" value={`${c.cancellation_rate}%`} icon={XCircle} />
        <Kpi testid="kpi-commission" label="Commissioni" value={eur(c.commission_paid)} icon={Euro} />
        <Kpi testid="kpi-inprogress" label="Ordini in corso" value={d.orders_in_progress} icon={Clock} />
      </div>

      <Card title="Andamento fatturato" icon={TrendingUp}>
        <div className="h-56" data-testid="analytics-revenue-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0A0A0C", border: "1px solid #ffffff20", borderRadius: 8 }} formatter={(v, n) => n === "revenue" ? [eur(v), "Fatturato"] : [v, "Ordini"]} />
              <Bar dataKey="revenue" fill="#00E676" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Prodotti più venduti" icon={Flame}>
          <div className="space-y-2" data-testid="analytics-top-products">
            {d.top_products.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="truncate"><span className="text-zinc-500 mr-2">{i + 1}.</span>{p.name}</span>
                <span className="text-zinc-400 shrink-0 ml-3">{p.qty}× · <span className="text-[#00E676]">{eur(p.revenue)}</span></span>
              </div>
            ))}
            {d.top_products.length === 0 && <div className="text-zinc-500 text-sm py-6 text-center">Nessuna vendita nel periodo.</div>}
          </div>
        </Card>
        <Card title="Categorie migliori" icon={Package}>
          <div className="space-y-2" data-testid="analytics-top-categories">
            {d.top_categories.map((cat, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="truncate">{cat.category}</span>
                <span className="text-zinc-400 shrink-0 ml-3">{cat.qty}× · <span className="text-[#00E676]">{eur(cat.revenue)}</span></span>
              </div>
            ))}
            {d.top_categories.length === 0 && <div className="text-zinc-500 text-sm py-6 text-center">—</div>}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Ordini per giorno" icon={ShoppingBag} right={<span className="text-[11px] text-zinc-500">Giorno top: <b className="text-[#00E676]">{d.busiest_weekday || "—"}</b></span>}>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.by_weekday}>
                <XAxis dataKey="weekday" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0A0A0C", border: "1px solid #ffffff20", borderRadius: 8 }} />
                <Bar dataKey="orders" fill="#7DF9FF" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Heatmap giorno × ora" icon={Flame} right={<span className="text-[11px] text-zinc-500">Ora top: <b className="text-[#00E676]">{d.busiest_hour || "—"}</b></span>}>
          <div className="overflow-x-auto" data-testid="analytics-heatmap">
            <div className="min-w-[440px]">
              <div className="flex gap-0.5 mb-0.5 pl-8">
                {HOURS.map((h) => <div key={h} className="w-4 text-[8px] text-zinc-600 text-center">{h}</div>)}
              </div>
              {WD.map((w, wi) => (
                <div key={wi} className="flex gap-0.5 mb-0.5 items-center">
                  <div className="w-8 text-[10px] text-zinc-500">{w}</div>
                  {HOURS.map((h) => {
                    const v = heatAt(wi, h);
                    const op = v ? 0.2 + 0.8 * (v / heatMax) : 0;
                    return <div key={h} title={`${w} ${h}:00 · ${v} ordini`} className="w-4 h-4 rounded-sm border border-white/5" style={{ background: v ? `rgba(0,230,118,${op})` : "#ffffff08" }} />;
                  })}
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card title="Clienti & fidelizzazione" icon={Users}>
          <div className="grid grid-cols-2 gap-3 text-sm" data-testid="analytics-customers">
            <div><div className="text-2xl font-bold">{d.customers.unique}</div><div className="text-xs text-zinc-500">Clienti unici</div></div>
            <div><div className="text-2xl font-bold text-[#00E676]">{d.customers.new}</div><div className="text-xs text-zinc-500">Nuovi</div></div>
            <div><div className="text-2xl font-bold text-cyan-300">{d.customers.returning}</div><div className="text-xs text-zinc-500">Abituali ({d.customers.returning_rate}%)</div></div>
            <div><div className="text-2xl font-bold">{d.customers.avg_orders_per_customer}</div><div className="text-xs text-zinc-500">Ordini / cliente</div></div>
          </div>
        </Card>
        <Card title="Tempi medi" icon={Clock}>
          <div className="space-y-3 text-sm" data-testid="analytics-times">
            <div className="flex items-center justify-between"><span className="text-zinc-400">Preparazione</span><span className="font-semibold">{d.avg_prep_time_min != null ? `${d.avg_prep_time_min} min` : "—"}</span></div>
            <div className="flex items-center justify-between"><span className="text-zinc-400">Consegna</span><span className="font-semibold">{d.avg_delivery_time_min != null ? `${d.avg_delivery_time_min} min` : "—"}</span></div>
          </div>
        </Card>
        <Card title="Canali di vendita" icon={ShoppingBag}>
          <div className="space-y-2 text-sm" data-testid="analytics-channels">
            {d.channels.payment.map((p, i) => (
              <div key={i} className="flex items-center justify-between"><span className="text-zinc-400 capitalize">{p.method}</span><span>{p.orders} ord · <span className="text-[#00E676]">{eur(p.revenue)}</span></span></div>
            ))}
            <div className="border-t border-white/10 my-1" />
            {d.channels.fulfillment.map((f, i) => (
              <div key={i} className="flex items-center justify-between"><span className="text-zinc-400 capitalize">{f.mode === "consegna" ? "Consegna" : "Ritiro"}</span><span>{f.orders} ord</span></div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Recensioni" icon={Star}>
          <div data-testid="analytics-reviews">
            <div className="flex items-center gap-2 mb-3"><span className="text-3xl font-bold text-amber-300">{d.reviews.avg_rating}</span><Star className="h-5 w-5 fill-amber-300 text-amber-300" /><span className="text-sm text-zinc-500">({d.reviews.count})</span></div>
            {[5, 4, 3, 2, 1].map((s) => {
              const n = d.reviews.distribution[String(s)] || 0;
              const pct = d.reviews.count ? (n / d.reviews.count) * 100 : 0;
              return <div key={s} className="flex items-center gap-2 mb-1 text-xs"><span className="w-3 text-zinc-500">{s}</span><div className="flex-1 h-1.5 rounded-full bg-white/10"><div className="h-full rounded-full bg-amber-300" style={{ width: `${pct}%` }} /></div><span className="w-6 text-right text-zinc-500">{n}</span></div>;
            })}
          </div>
        </Card>
        <Card title="Prodotti mai venduti" icon={Package}>
          <div className="space-y-1.5 text-sm" data-testid="analytics-zero-sales">
            {d.zero_sales_products.map((p, i) => (
              <div key={i} className="flex items-center justify-between"><span className="truncate text-zinc-400">{p.name}</span><span className="text-zinc-600">{eur(p.price)}</span></div>
            ))}
            {d.zero_sales_products.length === 0 && <div className="text-zinc-500 py-6 text-center">Ottimo: tutti i prodotti hanno venduto!</div>}
          </div>
        </Card>
      </div>

      <Card title="Visibilità" icon={Rocket}>
        <div className="flex items-center justify-between text-sm" data-testid="analytics-visibility">
          <span className="text-zinc-400">Livello attuale: <b className="text-white capitalize">{d.visibility.tier}</b></span>
          <span className="text-zinc-500">{d.visibility.orders_in_period} ordini · {eur(d.visibility.revenue_in_period)} nel periodo</span>
        </div>
      </Card>
    </div>
  );
}
